import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { usePriceBookWrite, type PriceBookWrite } from '../settings/usePriceBookWrite';
import { useOnline } from '../ui/useConnection';
import { PageRoot, PageHeader } from '../ui/Page';
import { SectionCard, useHashAnchor } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { money } from '../money';
import { useSession } from '../session';
import { Field, field } from '../create/Field';
import { EditSheet, DangerAction, changedFields, toField } from '../ui/EditSheet';
import { VendorPartEditor } from '../inventory/VendorPartEditor';
import { IconMoney } from '../icons';

/**
 * Products & services (CRM-18): the price book on a page of its own in Business, beside
 * Inventory. No new object: the price book already holds services, labor, materials and fees,
 * and how it prices things stays with Inventory v2. The card and its edit sheet moved here
 * from Settings unchanged, under the anchor they had there, so the old `/settings#price-book`
 * link lands on the same card (`movedFromSettings.ts`).
 *
 * Owner and office only, at the route (`OFFICE_ONLY`) and at every `priceBook.*` procedure:
 * it is every sell price the shop charges.
 */
export function ProductsPage() {
  // The router lands `#price-book`; this holds it there while the page settles, which WebKit
  // (no CSS scroll anchoring) would not do by itself. As on Settings.
  useHashAnchor();
  return (
    <PageRoot>
      <PageHeader
        title="Products & services"
        sub="What you sell, priced once: it fills in every quote and invoice."
      />

      <PriceBookCard id="price-book" />
    </PageRoot>
  );
}

/**
 * The price book — the things you sell, priced once. Every line editor reads this list,
 * so a shop that fills it in stops retyping "Service call — $89" forty times a month.
 * Unlimited, unlike Jobber's tiered caps.
 */
export function PriceBookCard({ id }: { id: string }) {
  const session = useSession(),
    write = usePriceBookWrite();
  if (session.role !== 'owner' && session.role !== 'office')
    return (
      <SectionCard id={id} title="Price book">
        <p>Only the owner and office can view catalogue prices.</p>
      </SectionCard>
    );
  return (
    <PriceBookOffice
      key={`${session.orgId}:${session.userId}:${session.role}`}
      id={id}
      write={write}
    />
  );
}
function PriceBookOffice({ id, write }: { id: string; write: PriceBookWrite }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const items = useQuery(trpc.priceBook.list.queryOptions());
  const [adding, setAdding] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(
    () => write.attempt?.input.itemId ?? null,
  );
  const [closeBlocked, setCloseBlocked] = useState(false);
  useEffect(() => {
    if (write.saved) {
      setEditingItem(null);
      setCloseBlocked(false);
    }
  }, [write.saved]);
  const [stockWarning, setStockWarning] = useState<string | null>(null);
  const [kind, setKind] = useState<'service' | 'labor' | 'material' | 'fee'>('service');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');

  const create = useMutation(
    trpc.priceBook.create.mutationOptions({
      onSuccess: () => {
        setName('');
        setPrice('');
        setAdding(false);
        void qc.invalidateQueries();
      },
    }),
  );

  const cents = Math.round(Number(price || 0) * 100);
  const valid = name.trim().length > 0 && cents > 0;
  const priceBookState = shownState(items, items.data);
  const inputClass =
    'h-10 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm';

  return (
    <SectionCard
      id={id}
      title={counted('Price book', priceBookState, items.data?.length ?? 0)}
      right={
        /**
         * ⚠️ Only once the list is KNOWN.
         *
         * 🔴 Found in a browser, not by a test: with every query failing, "Couldn't load
         * your price book" rendered under a live "Add item". Creating a row while the list
         * that would have shown it is unknown is how a shop ends up with two of something
         * it cannot see — the exact argument already written into
         * `CustomFieldsSettingsCard`, which I applied there and to none of its three
         * siblings. One of N parallel paths, again, committed inside the ticket about it.
         */
        priceBookState === 'ready' ? (
          <button
            type="button"
            disabled={write.locked}
            onClick={() => setAdding((v) => !v)}
            className="text-xs font-semibold text-[var(--sw-brand-text)] hover:underline"
          >
            Add item
          </button>
        ) : null
      }
    >
      {priceBookState === 'ready' && stockWarning ? (
        <Notice tone="warn">{stockWarning}</Notice>
      ) : null}
      {priceBookState === 'error' || priceBookState === 'offline' ? (
        <UnresolvedState
          state={priceBookState}
          what="your price book"
          icon={IconMoney}
          error={items.error}
          onRetry={() => void items.refetch()}
        />
      ) : (
        <>
          <StaleNotice
            show={refreshFailed(items, items.data)}
            onRetry={() => void items.refetch()}
          />
          {adding ? (
            <form
              className="mb-3 grid gap-2 rounded-[var(--radius-sm)] border border-border bg-surface-2 p-3 sm:grid-cols-[130px_1fr_120px_auto]"
              onSubmit={(e) => {
                e.preventDefault();
                if (!valid) return;
                create.mutate({
                  kind,
                  name: name.trim(),
                  unitPriceCents: cents,
                  ...idem(),
                });
              }}
            >
              <select
                className={inputClass}
                value={kind}
                aria-label="Item type"
                onChange={(e) =>
                  setKind(e.target.value as 'service' | 'labor' | 'material' | 'fee')
                }
              >
                <option value="service">Service</option>
                <option value="labor">Labor</option>
                <option value="material">Material</option>
                <option value="fee">Fee</option>
              </select>
              <input
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Service call — diagnostic"
                aria-label="Item name"
                autoFocus
              />
              <input
                className={inputClass}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="$ price"
                inputMode="decimal"
                aria-label="Item price"
              />
              <Button type="submit" disabled={!valid || create.isPending}>
                {create.isPending ? 'Saving…' : 'Save'}
              </Button>
            </form>
          ) : null}

          {priceBookState === 'loading' ? (
            <Skeleton className="h-16" />
          ) : (items.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing saved yet. Add the work you quote most often and it will autofill on
              every quote and invoice.
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {items.data!.map((i) => (
                <li key={i.id} className="flex items-center gap-3 py-2">
                  <StatusBadge tone="neutral">{i.kind}</StatusBadge>
                  <span className="min-w-0 flex-1 truncate">{i.name}</span>
                  <span className="font-medium tabular-nums">
                    {money(i.unitPriceCents)}
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (write.locked) return;
                      setStockWarning(null);
                      setEditingItem(i.id);
                    }}
                  >
                    Edit
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {closeBlocked ? (
        <Notice tone="warn">
          This catalogue edit is unresolved. Use its retry or refresh controls before
          closing.
        </Notice>
      ) : null}
      {editingItem &&
      (write.attempt?.item || items.data?.some((i) => i.id === editingItem)) ? (
        <EditPriceItemSheet
          key={editingItem}
          item={write.attempt?.item ?? items.data!.find((i) => i.id === editingItem)!}
          write={write}
          onClose={() => {
            if (write.locked) {
              setCloseBlocked(true);
              return;
            }
            setEditingItem(null);
          }}
          onRetired={setStockWarning}
        />
      ) : null}
    </SectionCard>
  );
}

/**
 * Change a price, or stop selling something.
 *
 * Editing here changes what the NEXT quote or invoice costs and never reaches back: lines
 * copy the name, price and tax flag at the moment they're added, exactly so a price rise
 * can't rewrite what a customer was already quoted.
 */
function EditPriceItemSheet({
  item: openedItem,
  write,
  onClose,
  onRetired,
}: {
  write: PriceBookWrite;
  item: {
    id: string;
    kind: string;
    name: string;
    description: string | null;
    unitLabel: string | null;
    unitPriceCents: number;
    taxable: boolean;
    category: string | null;
  };
  onClose: () => void;
  onRetired: (warning: string | null) => void;
}) {
  const [item] = useState(openedItem);
  const online = useOnline();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const retirement = useQuery(
    trpc.priceBook.retirementCheck.queryOptions({ id: item.id }),
  );
  const retirementState = shownState(retirement, retirement.data);

  const original = {
    name: item.name,
    description: toField(item.description),
    unitLabel: toField(item.unitLabel),
    category: toField(item.category),
    price: (item.unitPriceCents / 100).toFixed(2),
  };
  const [draft, setDraft] = useState(original);
  const [taxable, setTaxable] = useState(item.taxable);
  const [editingVendorPart, setEditingVendorPart] = useState(false);
  const set = (k: keyof typeof original) => (v: string) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const changed = changedFields(original, draft);
  const priceCents = Math.round(Number(draft.price || 0) * 100);
  const priceMoved = priceCents !== item.unitPriceCents;
  const dirty = Object.keys(changed).length > 0 || taxable !== item.taxable;

  const done = () => {
    void qc.invalidateQueries();
    onClose();
  };

  const retire = useMutation(
    trpc.priceBook.retire.mutationOptions({
      onSuccess: (answer) => {
        onRetired(answer.stockWarning);
        done();
      },
    }),
  );

  return (
    <EditSheet
      open
      onClose={onClose}
      title={`Edit ${item.name}`}
      dirty={dirty && !write.locked}
      saveDisabled={write.locked || !online || editingVendorPart}
      error={retire.error}
      onSave={() => {
        const { price: _price, ...rest } = changed;
        write.begin(
          {
            itemId: item.id,
            ...rest,
            ...(priceMoved ? { unitPriceCents: priceCents } : {}),
            ...(taxable !== item.taxable ? { taxable } : {}),
          },
          item,
        );
      }}
      destructive={
        !write.locked ? (
          <DangerAction
            label="Stop offering this"
            question={`Retire ${item.name}?`}
            detail={[
              "It stops appearing when you build a quote or an invoice. Everything you've already sold with it is untouched.",
              retirementState === 'offline'
                ? "Couldn't check stock balances while offline. Any balances stay in Inventory and their valuation treatment is unchanged."
                : retirementState === 'error'
                  ? "Couldn't check stock balances. Any balances stay in Inventory and their valuation treatment is unchanged."
                  : retirementState === 'loading'
                    ? 'Checking stock balances. Any balances stay in Inventory and their valuation treatment is unchanged.'
                    : retirement.data?.stockWarning,
            ]
              .filter(Boolean)
              .join(' ')}
            pending={retire.isPending}
            onConfirm={() => retire.mutate({ id: item.id, ...idem() })}
          />
        ) : null
      }
    >
      <PriceBookWriteFeedback write={write} />
      <fieldset
        disabled={write.locked}
        className="space-y-3 [&_input]:min-h-11 [&_select]:min-h-11 [&_button]:min-h-11"
      >
        {item.kind === 'material' ? (
          <Button variant="ghost" onClick={() => setEditingVendorPart(true)}>
            Vendor part numbers
          </Button>
        ) : null}
        {editingVendorPart ? (
          <VendorPartEditor
            itemId={item.id}
            itemName={item.name}
            onClose={() => setEditingVendorPart(false)}
          />
        ) : null}
        <StaleNotice
          show={refreshFailed(retirement, retirement.data)}
          onRetry={() => void retirement.refetch()}
        />
        <Field label="Name">
          <input
            className={field}
            value={draft.name}
            onChange={(e) => set('name')(e.target.value)}
            autoFocus
          />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Price">
            <input
              className={field}
              value={draft.price}
              onChange={(e) => set('price')(e.target.value)}
              inputMode="decimal"
            />
          </Field>
          <Field label="Per (unit)">
            <input
              className={field}
              value={draft.unitLabel}
              onChange={(e) => set('unitLabel')(e.target.value)}
              placeholder="hour, each, ft"
            />
          </Field>
        </div>
        <Field label="Category">
          <input
            className={field}
            value={draft.category}
            onChange={(e) => set('category')(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <textarea
            className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
            value={draft.description}
            onChange={(e) => set('description')(e.target.value)}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={taxable}
            onChange={(e) => setTaxable(e.target.checked)}
          />
          <span>Sales tax applies</span>
        </label>
        {priceMoved ? (
          <p className="text-xs text-muted-foreground">
            Quotes and invoices you've already written keep the old price. This only
            changes what gets added from now on.
          </p>
        ) : null}
      </fieldset>
    </EditSheet>
  );
}

function PriceBookWriteFeedback({ write }: { write: PriceBookWrite }) {
  const a = write.attempt;
  if (!a) return null;
  return (
    <div role="status" aria-live="polite" className="space-y-2 text-sm">
      <Notice tone="info">
        {a.accepted
          ? 'Accepted. Confirming saved catalogue prices…'
          : a.phase === 'writing'
            ? 'Saving catalogue edit…'
            : a.phase === 'reconcile'
              ? 'Office reconciliation is required. The original edit is retained.'
              : 'Resolve this edit before changing or closing the form.'}
      </Notice>
      {a.error ? <p className="break-words">{a.error}</p> : null}
      {a.phase === 'retry' ? (
        <Button className="min-h-11" onClick={write.retry}>
          Retry same edit
        </Button>
      ) : null}
      {a.phase === 'review' ? (
        <Button className="min-h-11" onClick={() => void write.review()}>
          Refresh and review
        </Button>
      ) : null}
      {a.phase === 'read_failed' ? (
        <Button className="min-h-11" onClick={() => void write.refresh()}>
          Retry refresh
        </Button>
      ) : null}
    </div>
  );
}
